import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Modal } from 'react-bootstrap'
import 'react-time-picker/dist/TimePicker.css'
import 'react-clock/dist/Clock.css'
import { useDropzone } from 'react-dropzone'
import { useFormik } from 'formik'
import Swal from 'sweetalert2'
import { utilityBulkUpload } from './Property_Api'
import * as XLSX from 'xlsx';
import * as Yup from 'yup'

interface excelSheetDataType {
    "Tenant Email ID": string
    "Utility": string
    "Utility Type": string
    "Month": string
    "Year": string
    "Amount": string
    "GST Applicable": string
    "CGST": string
    "SGST": string
}

const modalsRoot = document.getElementById('root-modals') || document.body

interface BulkUploadModalDataType {
    show: Boolean
    handleClose: () => void
    refreshTableAPI: () => void
}

const BulkUtilityUploadModal = ({ show, handleClose, refreshTableAPI }: BulkUploadModalDataType) => {
    const errorRef = useRef<HTMLDivElement>(null)
    const modalHeaderRef = useRef<HTMLDivElement>(null)
    const [uploadDocumentLoading, setUploadDocumentLoading] = useState<boolean>(false)
    const [error, setError] = useState<string>("")

    // dropzone code start
    const { acceptedFiles, fileRejections, getRootProps, getInputProps, isDragActive } = useDropzone({
        maxFiles: 1,
        accept: {
            'application/vnd.ms-excel': [],
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': []
        },
    })

    const acceptedFileItems = acceptedFiles.map((file) => {
        return (
            // @ts-ignore
            <li key={file.path}>
                {
                    // @ts-ignore
                    file.path
                }{' '}
                - {file.size} bytes
            </li>
        )
    })

    const fileRejectionItems = fileRejections.map(({ file, errors }) => (
        // @ts-ignore
        <li key={file.path}>
            {
                // @ts-ignore
                file.path
            }{' '}
            - {file.size} bytes
            <ul>
                {errors.map((e) => (
                    <li key={e.code}>{e.message}</li>
                ))}
            </ul>
        </li>
    ))
    // dropzone code ends

    const allowedValues = ['Electric Bill:377c88a73c2140e9a1646af33b57fc7d', 'Water Bill:7bb1f3d8723c4370b030455b0d9cddb8', 'Miscellaneous:93a91aaf6d1646d1a2702457f99d6403'];
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

    const validationSchema = Yup.object().shape({
        'Tenant Email ID': Yup.string()
            .email('Wrong email format')
            .min(3, 'Minimum 3 symbols')
            .max(50, 'Maximum 50 symbols')
            .required('Tenant email ID is required'),
        Utility: Yup.string().required('Utility is required'),
        'Utility Type': Yup.string().oneOf(allowedValues).required('Utility type is required'),
        Month: Yup.string().oneOf(months).required('Month is required'),
        Year: Yup.number()
            .typeError('Year must be a number')
            .positive('Year must be a positive number')
            .integer('Year must be an integer')
            .required('Year is required'),
        Amount: Yup.number()
            .typeError('Amount must be a number')
            .positive('Amount must be a positive number')
            .integer('Amount must be an integer')
            .required('Amount is required'),
        'GST Applicable': Yup.boolean().required('GST Applicable is required'),
        CGST: Yup.number()
            .typeError('CGST must be a number')
            .positive('CGST must be a positive number')
            .required('CGST is required'),
        SGST: Yup.number()
            .typeError('SGST must be a number')
            .positive('SGST must be a positive number')
            .required('SGST is required'),
    });

    const bulkUtilityUpload = () => {
        setUploadDocumentLoading(true)
        const reader = new FileReader()
        reader.onloadend = async () => {
            const base64String = reader.result
            try {
                const { data: res } = await utilityBulkUpload(
                    // @ts-ignore
                    base64String.split(',')[1]
                )
                if (res.code === "200") {
                    acceptedFiles.pop()
                    setUploadDocumentLoading(false)
                    handleClose()
                    refreshTableAPI()
                    Swal.fire({
                        position: 'center',
                        icon: 'success',
                        title: res.msg.charAt(0).toUpperCase() + res.msg.slice(1),
                        text: res.msg_details,
                        allowOutsideClick: true,
                    })
                }
                else {
                    setUploadDocumentLoading(false)
                    setError(`${res.msg.charAt(0).toUpperCase() + res.msg.slice(1)}: ${res.msg_details}`)
                }
            }
            catch (error: any) {
                setUploadDocumentLoading(false)
                setError(`Error: ${error}`)
            }
        }
        reader.readAsDataURL(acceptedFiles[0])
    }

    const handleSubmit = () => {
        if (acceptedFiles.length === 1) {
            (async () => {
                const ab = await acceptedFiles[0].arrayBuffer();
                const excelSheet = XLSX.read(ab);
                const ws = excelSheet.Sheets[excelSheet.SheetNames[0]]; // get the first worksheet
                const data: excelSheetDataType[] = XLSX.utils.sheet_to_json(ws); // generate objects
                const requiredColumns: string[] = ['Tenant Email ID', 'Utility', 'Utility Type', 'Month', 'Year', 'Amount', 'GST Applicable', 'CGST', 'SGST'];
                if (data && data.length > 0) {
                    const columnsPresent: string[] = data && data.length > 0 ? Object.keys(data[0]) : [];
                    if (requiredColumns.length !== columnsPresent.length) {
                        setError("Error: Please recheck the excel file to ensure all columns align with the standard template and are mandatory")
                        return;
                    }

                    // Check if each element in the arrays is equal
                    for (let k = 0; k < requiredColumns.length; k++) {
                        if (requiredColumns[k] !== columnsPresent[k]) {
                            setError("Error: Please recheck the excel file to ensure all columns align with the standard template and are mandatory")
                            return;
                        }
                    }

                    const arrayOfObjectsSchema = Yup.array().of(validationSchema);
                    arrayOfObjectsSchema.validate(data)
                        .then(valid => bulkUtilityUpload())
                        .catch(err => setError(err.toString())
                        );
                }
                else {
                    setError("Error: Excel sheet is empty")
                }
            })();
        } else {
            setError(`Error: No document found. Add excel sheet to upload`)
        }
    }

    // wipe out the error every 2 sec
    useEffect(() => {
        if (error) {
            setTimeout(() => {
                setError('')
            }, 3000)
        }
    }, [error])

    const initialValues = {
        file: '',
    }

    const formik = useFormik({
        initialValues,
        onSubmit: () => handleSubmit(),
    })

    return createPortal(
        <Modal
            id='kt_modal_create_app'
            tabIndex={-1}
            aria-hidden='true'
            dialogClassName='modal-dialog modal-dialog-centered mw-900px'
            show={show ? true : false}
            onHide={handleClose}
            backdrop={true}
        >
            {/* Begin: Modal header */}
            <div className='modal-header pt-5 pb-5' ref={modalHeaderRef}>
                {/* Begin: Modal Heading */}
                <h2>Bulk Utility Upload</h2>
                {/* End: Modal Heading */}
                {/* Begin::Close button */}
                <div className='btn btn-sm btn-icon btn-active-color-primary' onClick={handleClose}>
                    <i className='bi bi-x-lg' onClick={handleClose}></i>
                </div>
                {/* end::Close button */}
            </div>
            {/* End: Modal header */}
            {/* Begin: Error msg section */}
            {error && (
                <div ref={errorRef} className='d-flex justify-content-center align-items-center px-2' style={{ backgroundColor: 'red' }}>
                    <span className='fv-help-block' style={{ color: 'white' }}>
                        {error}
                    </span>
                </div>
            )}
            {/* End: Error msg section */}
            {/* Begin: Modal body */}
            <div className='modal-body'>
                <div id='kt_account_profile_details' className='collapse show'>
                    <form onSubmit={formik.handleSubmit} className='form'>
                        <div className='card-body'>
                            {/*begin::Dropzone field */}
                            <label className='d-flex align-items-center fs-5 fw-semibold mb-2 ps-6'>
                                <span className='required'>Upload Utility Excel Sheet</span>
                                <i
                                    className='fas fa-exclamation-circle ms-2 fs-7'
                                    data-bs-toggle='tooltip'
                                    title='Upload utility excel sheet'
                                ></i>
                            </label>
                            <div className='border border-2 rounded border-dashed row p-2 bg-white mx-4'>
                                <section className='border border-2 rounded border-primary border-dashed h-100 symbol bg-light-primary'>
                                    <div
                                        {...getRootProps({
                                            className:
                                                'py-4 d-flex flex-column justify-content-center align-items-center',
                                        })}
                                    >
                                        <input {...getInputProps()} />
                                        {isDragActive ? (
                                            <p>Drop the files here...</p>
                                        ) : (
                                            <p>Drag and drop files here, or click to select files</p>
                                        )}
                                        <i className='bi bi-file-earmark-arrow-up-fill fs-4x btn btn-active-color-primary py-0 mb-3'></i>
                                        <span className='text-primary fs-7'>
                                            Type: *.xls or *.xlsx only
                                        </span>
                                        <span className='text-primary fs-7'>Max document:1</span>
                                    </div>
                                </section>
                            </div>

                            <aside className='px-5'>
                                {acceptedFileItems.length > 0 && (
                                    <div className='pt-2'>
                                        <h4>Document is ready to upload</h4>
                                        <ul>{acceptedFileItems}</ul>
                                    </div>
                                )}
                                {fileRejectionItems.length > 0 && (
                                    <div className='pt-2'>
                                        <span className='fv-help-block color-red'>
                                            Upload only one file or select files with allowed formats only
                                        </span>
                                        {/* <ul>{fileRejectionItems}</ul> */}
                                    </div>
                                )}
                            </aside>
                            {/*end::Dropzone field */}
                            <div className='d-flex flex-column align-items-center'>
                                <button
                                    type='submit'
                                    className='btn btn-sm btn-primary shadow mt-3'
                                    style={{ cursor: 'pointer' }}
                                    disabled={uploadDocumentLoading}
                                >
                                    {uploadDocumentLoading ? (
                                        <span className='indicator-progress' style={{ display: 'block' }}>
                                            Processing...
                                            <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                                        </span>
                                    ) : (
                                        <span>Upload Excel Sheet</span>
                                    )}
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </Modal>,
        modalsRoot
    )
}
export default BulkUtilityUploadModal